import React, {Component} from 'react';
import {Button, MenuItem} from '@material-ui/core';
import {Booking} from 'services/entities/booking';
import Form, {FormStatus} from 'components/forms/Form';
import {ValidationGroup, Validators} from 'util/validation';
import ProgressSpinner from 'components/ProgressSpinner';
import {handleError, NotFoundError} from 'util/errors';
import {SuccessIcon} from 'components/Icon';
import {NewSpaceEvent, SpaceEvent, SpaceEventType, UpdatedSpaceEvent} from 'services/entities/events';
import NumberField from 'components/forms/NumberField';
import TextField from 'components/forms/TextField';
import SelectField from 'components/forms/SelectField';
import ImageUploadField from 'components/forms/ImageUploadField';
import {createBookingEvent, loadBookingEvent, updateBookingEvent} from 'services/events';
import RadioGroupField from 'components/forms/RadioGroupField';
import Modal from 'components/Modal';
import EventCard from 'components/events/EventCard';
import './index.scss';
import {Space} from 'services/entities/spaces';
import {loadSpace} from 'services/spaces';
import {CurrencyAmount} from 'util/currency';
import {tryParseURL} from 'util/url';

interface BookingEventFormState {
  status: FormStatus;
  loadingEvent: boolean;
  event: SpaceEvent|null;
  previewing: boolean;
  previewEvent: SpaceEvent|null;
  bookingSpace: Space|null;
}

interface BookingEventFormProps {
  booking: Booking;
}

interface BookingEventFormModel {
  name: string,
  description: string,
  type: SpaceEventType,
  heroImage: URL|File|null,
  entryCost: number|null,
  entryFree: string,
  link: string
}

export default class BookingEventForm extends Component<BookingEventFormProps, BookingEventFormState> {
  state: BookingEventFormState = {
    status: FormStatus.None,
    loadingEvent: true,
    event: null,
    previewing: false,
    previewEvent: null,
    bookingSpace: null
  };

  private readonly validation: ValidationGroup;
  private readonly model:BookingEventFormModel = {
    name: '',
    description: '',
    type: SpaceEventType.Event,
    heroImage: null,
    entryCost: 0,
    entryFree: 'true',
    link: ''
  };

  constructor(props:BookingEventFormProps) {
    super(props);
    this.validation = new ValidationGroup(this.model);
    this.validation.addField('name', {'required': Validators.required});
    this.validation.addField('description', {'required': Validators.required});
    this.validation.addField('type', {'required': Validators.required});
    this.validation.addField('heroImage', { 'maxFileSize': Validators.maxFileSize(2 * 1024 * 1024) });
    this.validation.addField('entryCost', {});
    this.validation.addField('entryFree', {});
    this.validation.addField('link', { 'format': Validators.url });
  }

  componentDidMount() {
    loadBookingEvent(this.props.booking.code).then(
      (event) => {
        this.model.name = event.name;
        this.model.description = event.description;
        this.model.type = event.type;
        this.model.entryCost = event.entryCost?.amount || null;
        this.model.entryFree = (this.model.entryCost || 0) === 0 ? 'true' : 'false';
        this.model.heroImage = event.heroImage;
        this.model.link = event.link ? event.link.toString() : '';
        this.validation.validate().then(
          () => this.setState({event: event, loadingEvent: false})
        );
      },
      ex => {
        if(ex instanceof NotFoundError) {
          this.setState({loadingEvent: false});
        } else {
          this.setState({loadingEvent: false});
          handleError("Failed loading event", ex);
        }
      }
    );
    loadSpace(this.props.booking.space).then(
      space => this.setState({bookingSpace: space}),
      ex => handleError("Failed loading space", ex)
    );
  }

  componentWillUnmount() {
    const previewEvent = this.state.previewEvent;
    if(previewEvent && previewEvent.heroImage) {
      URL.revokeObjectURL(previewEvent.heroImage.toString());
    }
  }

  onDescriptionInput = () => {
    // force update of character count
    this.forceUpdate();
  };

  onPreviewClick = () => {
    const {link, heroImage, entryCost, entryFree, ...eventProps} = this.model;
    const booking = this.props.booking;
    let heroImageURL: URL|null = null;
    if(heroImage) {
      if(heroImage instanceof URL) {
        heroImageURL = heroImage;
      } else {
        heroImageURL = new URL(URL.createObjectURL(heroImage));
      }
    }
    const entryCostAmount = entryFree !== 'true' && entryCost ? new CurrencyAmount(entryCost) : null;
    const event: SpaceEvent = {
      code: '',
      startTime: booking.startTime,
      endTime: booking.endTime,
      space: this.state.bookingSpace || { code: this.props.booking.space, name: '' },
      heroImage: heroImageURL,
      link: link ? tryParseURL(link) : null,
      entryCost: entryCostAmount,
      ...eventProps
    };
    this.setState({previewing: true, previewEvent: event });
  };

  onEventModalClosed = () => {
    const previewEvent = this.state.previewEvent;
    if(previewEvent && previewEvent.heroImage) {
      URL.revokeObjectURL(previewEvent.heroImage.toString());
    }
    this.setState({previewing: false});
  }

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    const { link, entryFree, entryCost, heroImage, ...eventProps } = this.model;
    let promise: Promise<SpaceEvent>;
    if(this.state.event) {
      const event: UpdatedSpaceEvent = {
        ...eventProps,
        heroImage: heroImage instanceof File ? heroImage : (heroImage ? undefined : null),
        entryCost: entryFree === 'true' ? 0 : entryCost,
        link: link ? tryParseURL(link) : null
      };
      promise = updateBookingEvent(event, this.props.booking.code);
    } else {
      const event: NewSpaceEvent = {
        ...eventProps,
        heroImage: heroImage instanceof File ? heroImage : null,
        entryCost: entryFree === 'true' ? 0 : entryCost,
        link: link ? new URL(link) : null
      };
      promise = createBookingEvent(event, this.props.booking.code);
    }
    promise.then(
      () => this.setState({status: FormStatus.Success}),
      (ex) => {
        this.setState({status: FormStatus.Failure});
        handleError('Failed to save event', ex);
      }
    );
  };

  renderSuccessMessage() {
    return <div className="event-form success">
      <h3><SuccessIcon/> Your event has been saved.</h3>
    </div>;
  }

  renderLoading() {
    return <div className="event-form loading">
      <ProgressSpinner />
    </div>;
  }

  renderForm() {
    const currency = this.props.booking.currency;
    const remainingCharacterCount = 500 - this.model.description.length;
    const enabled = this.state.status === FormStatus.None || this.state.status === FormStatus.Failure;
    const { previewing, previewEvent } = this.state;
    return <Form className="event-form" validationGroup={this.validation} onSubmit={this.onSubmit}>
      <label>Name your event</label>
      <TextField name="name" validationGroup={this.validation} model={this.model} maxLength={200}
                 disabled={!enabled} required={true} validationMessages={{'required': 'Name is required'}} />
      <label>What type of event are you creating?</label>
      <SelectField disabled={!enabled} name="type" model={this.model}
                   validationGroup={this.validation} validationMessages={{'required': 'Event type is required'}}>
        <MenuItem value={SpaceEventType.Event}>Event</MenuItem>
        <MenuItem value={SpaceEventType.Retail}>Retail</MenuItem>
      </SelectField>
      <label className="description-label">
        Event description
        <span className="character-count">{ remainingCharacterCount } characters remaining</span>
      </label>
      <TextField name="description" validationGroup={this.validation} model={this.model}
                 disabled={!enabled} required={true} validationMessages={{'required': 'Event description is required'}}
                 multiline={true} maxLength={500} onInput={this.onDescriptionInput} />
      <label>Add a photo <span className="optional">(Optional)</span></label>
      <ImageUploadField name="heroImage" validationGroup={this.validation} model={this.model} variant="expanded"
                        validationMessages={{maxFileSize: 'Photo cannot be larger than 2 MB'}} disabled={!enabled} />
      <label>Entry fee</label>
      <RadioGroupField className="entry-cost" name="entryFree" model={this.model} validationGroup={this.validation}
                       options={[{value: 'true', label: 'Free'}, {value: 'false', label: <>
                        <span className="currency">{ currency.symbol }</span>
                          <NumberField name="entryCost" validationGroup={this.validation} model={this.model}
                                 disabled={!enabled} minValue={1} maxValue={1000} />
                        </>}]} />
      <label>Website / Social medial URL <span className="optional">(Optional)</span></label>
      <TextField name="link" validationGroup={this.validation} model={this.model}
                 disabled={!enabled} validationMessages={{'format': 'Website must be a valid URL'}} />
      <div className="actions">
        <Button className="preview" onClick={this.onPreviewClick}>View preview</Button>
        <Button type="submit" disabled={!enabled}>
          {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
          Submit
        </Button>
      </div>
      <Modal open={previewing} onClosing={this.onEventModalClosed} className="event-modal">
        { previewEvent && <EventCard event={previewEvent} cssClass="large" /> }
      </Modal>
    </Form>;
  }

  render() {
    if(this.state.loadingEvent) {
      return this.renderLoading();
    } else if(this.state.status === FormStatus.Success) {
      return this.renderSuccessMessage();
    } else {
      return this.renderForm();
    }
  }
}